// Detecta emails con el dominio mal escrito (gamil.com, hotmial.com, gmail.con…) y propone la corrección.
// Solo mira el dominio: un error antes de la @ no se puede detectar.

// Ordenados por uso: ante un empate de parecido gana el primero.
const DOMINIOS_HABITUALES = [
    'gmail.com',
    'hotmail.com',
    'hotmail.es',
    'outlook.com',
    'outlook.es',
    'yahoo.es',
    'yahoo.com',
    'icloud.com',
    'live.com',
    'live.es',
    'msn.com',
    'telefonica.net',
    'movistar.es',
    'orange.es',
    'vodafone.es',
    'terra.es',
    'ono.com',
    'me.com',
    'mac.com',
    'googlemail.com',
    'ymail.com',
    'mail.com',
    'gmx.com',
    'gmx.es',
    'protonmail.com',
    'proton.me',
];

// Dominios que la distancia no resuelve bien pero que nunca son correctos.
const CORRECCIONES_FIJAS: Record<string, string> = {
    'gmail.es': 'gmail.com',
    'gmail.com.es': 'gmail.com',
    'googlemail.es': 'googlemail.com',
    'icloud.es': 'icloud.com',
};

// Terminaciones de dos letras que casi siempre son un .com mal escrito, no un país.
const TERMINACIONES_ERRONEAS = ['co', 'cm', 'om'];

// Separa el dominio en lo que va antes y después del último punto (hotmail.fr → hotmail, fr).
const partes = (dominio: string): [string, string] => {
    const punto = dominio.lastIndexOf('.');
    return punto === -1 ? [dominio, ''] : [dominio.slice(0, punto), dominio.slice(punto + 1)];
};

// Distancia de edición contando como un solo error el cambio de orden de dos letras seguidas (gamil → gmail).
const distancia = (a: string, b: string): number => {
    const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
        Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
    );

    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            const coste = a[i - 1] === b[j - 1] ? 0 : 1;
            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + coste);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
            }
        }
    }

    return d[a.length][b.length];
};

// Devuelve el email con el dominio corregido, o null si no parece mal escrito.
export const sugerenciaEmail = (email?: string | null): string | null => {
    if (!email) return null;

    const limpio = email.trim();
    const arroba = limpio.lastIndexOf('@');
    if (arroba <= 0 || arroba === limpio.length - 1) return null;

    const local = limpio.slice(0, arroba);
    const dominio = limpio.slice(arroba + 1).toLowerCase();
    if (DOMINIOS_HABITUALES.includes(dominio)) return null;

    const fija = CORRECCIONES_FIJAS[dominio];
    if (fija) return `${local}@${fija}`;

    // En dominios cortos dos errores ya acercan a dominios reales distintos (mac.com, msn.com).
    const maximo = dominio.length >= 9 ? 2 : 1;
    let mejor: string | null = null;
    let mejorDistancia = maximo + 1;
    const [nombre, terminacion] = partes(dominio);
    const esPais = terminacion.length === 2 && !TERMINACIONES_ERRONEAS.includes(terminacion);

    for (const habitual of DOMINIOS_HABITUALES) {
        // El mismo proveedor con otro país (hotmail.fr, yahoo.de) es un dominio real.
        if (esPais && partes(habitual)[0] === nombre) continue;

        const dist = distancia(dominio, habitual);
        if (dist < mejorDistancia) {
            mejor = habitual;
            mejorDistancia = dist;
        }
    }

    return mejor ? `${local}@${mejor}` : null;
};
